import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from 'react'
import { describedBy } from './a11y'
import { WarningCircle } from './icons'

type ControlProps = {
  id?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean | 'true' | 'false'
}

export type FieldProps = {
  /** Title Case label. */
  label: string
  /** One form control (Input, Select, textarea). Field gives it an id and the aria wiring. */
  children: ReactElement<ControlProps>
  /** Sentence case help text under the control. */
  hint?: ReactNode
  /** Sentence case error message. When set, the control gets aria-invalid. */
  error?: ReactNode
  /** Shows an asterisk and tells screen readers the field is required. */
  required?: boolean
  className?: string
}

/** Wraps a form control with its label, hint and error, and wires their ids to the control. */
export function Field({ label, children, hint, error, required, className = '' }: FieldProps) {
  const generatedId = useId()
  const control = isValidElement(children) ? children : null
  const controlId = control?.props.id ?? `${generatedId}-control`
  const hintId = hint ? `${controlId}-hint` : undefined
  const errorId = error ? `${controlId}-error` : undefined
  const invalid = Boolean(error)

  return (
    <div className={['ui-field', invalid && 'ui-field--invalid', className].filter(Boolean).join(' ')}>
      <label htmlFor={controlId} className="ui-field__label">
        {label}
        {required && (
          <>
            <span className="ui-field__required" aria-hidden="true">*</span>
            <span className="ui-visually-hidden"> (required)</span>
          </>
        )}
      </label>
      {control && cloneElement(control, {
        id: controlId,
        'aria-invalid': invalid || control.props['aria-invalid'] || undefined,
        'aria-describedby': describedBy(control.props['aria-describedby'], hintId, errorId),
      })}
      {hint && <p id={hintId} className="ui-field__hint">{hint}</p>}
      {error && (
        <p id={errorId} className="ui-field__error">
          <WarningCircle size={16} aria-hidden="true" />
          <span>{error}</span>
        </p>
      )}
    </div>
  )
}
